import { useCallback, useEffect, useState } from 'react';
import {
  getTravefyLink,
  isTravefyDisabledError,
  createTravefyTicket,
  unlinkTravefyAccount,
  isSafeExternalUrl,
} from '../helpers/travefy';

/**
 * Loads the advisor's Travefy link state once. `isAvailable` is false until the
 * request succeeds, and stays false on 403 (feature off) or any error, so the UI
 * simply never renders the Travefy action.
 */
const useTravefyLink = (enabled) => {
  const [link, setLink] = useState(null);
  const [isAvailable, setIsAvailable] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    let isCancelled = false;
    getTravefyLink()
      .then((data) => {
        if (isCancelled) return;
        setLink(data);
        setIsAvailable(true);
      })
      .catch((error) => {
        if (isCancelled) return;
        setIsAvailable(false);
        if (!isTravefyDisabledError(error)) setLink(null);
      });
    return () => {
      isCancelled = true;
    };
  }, [enabled]);

  // Asks the API for a start URL (the return path travels inside the ticket), then
  // leaves for Travefy in this tab. Resolves true when navigation started.
  const connect = useCallback(async () => {
    const { startUrl } = await createTravefyTicket(
      `${window.location.pathname}${window.location.search}`,
    );
    if (!isSafeExternalUrl(startUrl)) return false;
    window.location.href = startUrl;
    return true;
  }, []);

  const disconnect = useCallback(async () => {
    const data = await unlinkTravefyAccount();
    setLink((prev) => ({ ...prev, linked: false, fullName: undefined, username: undefined }));
    const url = data?.unauthorizeUrl || link?.unauthorizeUrl;
    if (url && isSafeExternalUrl(url)) window.open(url, '_blank', 'noopener,noreferrer');
  }, [link]);

  return { link, isAvailable, connect, disconnect, setLink };
};

export default useTravefyLink;
